import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Box, Button, Chip, CircularProgress, Alert, Tooltip } from '@mui/material';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined';
import PictureAsPdfOutlinedIcon from '@mui/icons-material/PictureAsPdfOutlined';
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import TableChartOutlinedIcon from '@mui/icons-material/TableChartOutlined';
import AudioFileOutlinedIcon from '@mui/icons-material/AudioFileOutlined';
import VideoFileOutlinedIcon from '@mui/icons-material/VideoFileOutlined';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { APIURL } from '../../../config';

// Uploads files as artifacts on the task. Sequential on purpose: the API names
// each blob MAX(artifacts.id)+1, so parallel uploads would collide.
export const uploadTaskFiles = async (files, { task_id, customer_id, user_id }) => {
    for (const file of files) {
        const formData = new FormData();
        formData.append('file_name', file.name.replace(/(\.[^/.]+)+$/, ''));
        formData.append('task_id', task_id);
        if (customer_id) formData.append('cus_id', customer_id);
        if (user_id) formData.append('emp_id', user_id);
        formData.append('snapshot', file, file.name);

        const response = await axios.post(APIURL + '/artifacts', formData, { headers: { 'content-type': 'multipart/form-data' } });
        if (response.data?.code !== 200) throw new Error(response.data?.message || `Unable to upload ${file.name}`);
    }
};

const FILE_TYPES = [
    { exts: ['pdf'], Icon: PictureAsPdfOutlinedIcon, color: '#d32f2f' },
    { exts: ['png', 'jpg', 'jpeg', 'gif', 'bmp', 'webp', 'svg', 'heic', 'tif', 'tiff'], Icon: ImageOutlinedIcon, color: '#0288d1' },
    { exts: ['xls', 'xlsx', 'csv', 'ods'], Icon: TableChartOutlinedIcon, color: '#2e7d32' },
    { exts: ['doc', 'docx', 'txt', 'rtf', 'odt'], Icon: DescriptionOutlinedIcon, color: '#1565c0' },
    { exts: ['mp3', 'wav', 'm4a', 'aac', 'ogg'], Icon: AudioFileOutlinedIcon, color: '#7b1fa2' },
    { exts: ['mp4', 'mov', 'avi', 'webm', 'mkv'], Icon: VideoFileOutlinedIcon, color: '#7b1fa2' },
];

const fileType = (ext) =>
    FILE_TYPES.find(t => t.exts.includes(ext)) || { Icon: InsertDriveFileOutlinedIcon, color: '#616161' };

// Name and extension kept apart so a long name truncates without losing the extension
const splitName = (name) => {
    const match = name.match(/^(.*?)(\.[^/.]+)?$/);
    return match[1] ? { base: match[1], ext: match[2] || '' } : { base: name, ext: '' };
};

const FileChip = ({ name, href, onDelete }) => {
    const { base, ext } = splitName(name);
    const { Icon, color } = fileType(ext.replace('.', '').toLowerCase());
    const link = href ? { component: 'a', href, target: '_blank', rel: 'noopener noreferrer', clickable: true } : {};

    return (
        <Tooltip title={href ? `${name} (click to open)` : `${name} (uploads when the form is saved)`}>
            <Chip
                {...link}
                variant="outlined"
                onDelete={onDelete}
                icon={<Icon sx={{ '&&': { color } }} />}
                label={
                    <Box sx={{ display: 'flex', alignItems: 'center', minWidth: 0 }}>
                        <Box component="span" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{base}</Box>
                        <Box component="span" sx={{ flexShrink: 0, color: 'text.secondary' }}>{ext}</Box>
                        {href && <OpenInNewIcon sx={{ fontSize: 14, ml: 0.75, color: 'text.secondary', flexShrink: 0 }} />}
                    </Box>
                }
                sx={{
                    maxWidth: 260,
                    height: 34,
                    borderRadius: 2,
                    bgcolor: 'background.paper',
                    borderStyle: href ? 'solid' : 'dashed',
                    transition: 'border-color 0.15s, box-shadow 0.15s',
                    '&:hover': href ? { borderColor: color, boxShadow: '0 1px 4px rgba(0,0,0,0.12)' } : {},
                    '& .MuiChip-label': { display: 'flex', minWidth: 0 },
                }}
            />
        </Tooltip>
    );
};

const artifactName = (artifact) => {
    const ext = artifact.url?.match(/\.[^/.]+$/)?.[0] || '';
    return (artifact.file_name || 'File') + ext;
};

// Footer widget for form modals: lists the task's files (click to view/download)
// and, unless readOnly, lets the user attach more. Without a task_id (form not
// saved yet) picked files are held as pendingFiles for the parent to upload.
const FormAttachments = ({ task_id, customer_id, user_id, readOnly, pendingFiles, onPendingChange }) => {
    const [files, setFiles] = useState([]);
    const [uploading, setUploading] = useState(false);
    const [error, setError] = useState('');
    const inputRef = useRef(null);

    useEffect(() => {
        if (task_id) getFiles();
    }, [task_id]);

    const getFiles = async () => {
        try {
            const response = await axios.get(APIURL + '/artifacts', { params: { tas_id: task_id } });
            setFiles(Array.isArray(response.data) ? response.data : []);
        } catch (err) {
            console.error('Error fetching attachments:', err);
        }
    };

    const handleSelect = async (e) => {
        const selected = Array.from(e.target.files || []);
        e.target.value = '';
        if (!selected.length) return;
        setError('');

        if (!task_id) {
            onPendingChange && onPendingChange([...(pendingFiles || []), ...selected]);
            return;
        }

        setUploading(true);
        try {
            await uploadTaskFiles(selected, { task_id, customer_id, user_id });
        } catch (err) {
            console.error('Error uploading attachments:', err);
            setError(err.response?.data?.message || err.message || 'Unable to upload file. Please retry.');
        }
        setUploading(false);
        getFiles();
    };

    if (readOnly && !files.length) return null;

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, flex: 1, minWidth: 0 }}>
            {error && <Alert severity="error" onClose={() => setError('')}>{error}</Alert>}
            <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
                {!readOnly && (
                    <>
                        <input ref={inputRef} type="file" multiple hidden onChange={handleSelect} />
                        <Button
                            variant="contained"
                            color="secondary"
                            startIcon={uploading ? <CircularProgress size={16} /> : <AttachFileIcon />}
                            disabled={uploading}
                            onClick={() => inputRef.current?.click()}
                        >
                            Attach Files
                        </Button>
                    </>
                )}
                {files.map(file =>
                    <FileChip key={file.id} name={artifactName(file)} href={file.url} />
                )}
                {!task_id && (pendingFiles || []).map((file, idx) =>
                    <FileChip
                        key={`${file.name}-${idx}`}
                        name={file.name}
                        onDelete={() => onPendingChange && onPendingChange(pendingFiles.filter((_, i) => i !== idx))}
                    />
                )}
            </Box>
        </Box>
    );
};

export default FormAttachments;
